import { getRequestHeaders } from '@sgwt/react-shared-libs';
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import qs from 'qs';
import { getAccessToken } from '../../helpers';
import { getAxiosError } from '../../utils';
import {
  ApiRequestConfig,
  DEFAULT_API_TIMEOUT,
  DEFAULT_HEADER,
  Level,
  Logger,
  RepositoryConfig,
  RepositoryError,
  StringfyableParam,
  TIMEOUT_ERROR_MESSAGE,
} from './api.type';
const headers = getRequestHeaders();

const consoleLogger: Logger = {
  debug: (name, message) => {
    console.debug(name, message);
  },
  logError: (message: string, error?: Error, stack?: string): void => {
    console.error(message, `${error ?? ''}${stack ?? ''}`, Level.Error);
  },
  logWarning: (message: string): void => {
    console.warn(message, message, Level.Warn);
  },
};

const createAxiosInstance = (): AxiosInstance => {
  return axios.create({
    headers: { ...DEFAULT_HEADER, ...headers },
    timeout: DEFAULT_API_TIMEOUT,
    timeoutErrorMessage: TIMEOUT_ERROR_MESSAGE,
  });
};

const mapToErrorMessage = (error: AxiosError): string | null => {
  // Services can return the error message in different forms, here is some observed forms:
  // response.data, response.data.UserMessage, response.data.userMessage, response.data.message, response.data.title
  const data: any = error.response?.data;
  const strData: string | null = typeof data === 'string' ? data : null;
  const message =
    data?.UserMessage ??
    data?.userMessage ??
    data?.message ??
    data?.title ??
    strData ??
    error.message ?? // generic axios error
    'Unknown server error';

  return typeof message === 'string' ? message : null;
};

export function mapToRepositoryError(error: AxiosError): RepositoryError {
  const message = mapToErrorMessage(error);
  return {
    data: error.response?.data,
    etag: error.response?.headers?.['etag'],
    requestUrl: error.config?.url,
    status: error.response?.status,
    type: error.response?.status,
    message: message ?? error.response?.statusText ?? '',
    details: (error.response?.data as any)?.details,
  };
}

const apiErrorHandler = (logger: Logger) => {
  return (error?: AxiosError) => {
    if (!error?.isAxiosError) {
      return Promise.reject({ errorMessage: 'Unknown error' });
    }
    if (axios.isCancel(error)) {
      console.debug('Request was cancelled');
    } else {
      throw new Error(getAxiosError(error));
    }
  };
};

const apiRequestHandler = () => {
  return (request: AxiosRequestConfig) => {
    if (request?.headers) {
      request.headers.Authorization = getAccessToken() ?? '';
    }
    return request;
  };
};

const apiResponseHandler = () => {
  return (response: AxiosResponse) => {
    return response.data;
  };
};

export class ApiRepository {
  public readonly axiosClient: AxiosInstance;
  public readonly config?: RepositoryConfig | null;

  private constructor(config?: RepositoryConfig) {
    this.config = config ?? null;
    const { logger = consoleLogger } = config ?? {};
    const axiosInstance = createAxiosInstance();
    axiosInstance.interceptors.request.use(apiRequestHandler(), apiErrorHandler(logger));
    axiosInstance.interceptors.response.use(apiResponseHandler(), apiErrorHandler(logger));
    this.axiosClient = axiosInstance;
  }

  static Core(): ApiRepository {
    return new ApiRepository();
  }

  get<T>(url: string, params?: Record<string, StringfyableParam>, config?: ApiRequestConfig): Promise<T> {
    return this.axiosClient.get<T, T>(url, {
      params,
      paramsSerializer: {
        serialize: (params) => qs.stringify(params, { arrayFormat: 'comma' }),
      },
      ...config,
    });
  }

  put<U, T>(url: string, body: U, params?: Record<string, StringfyableParam>, config?: ApiRequestConfig): Promise<T> {
    return this.axiosClient.put<T, T>(url, body, { params, ...config });
  }

  post<U, T>(url: string, body: U, params?: Record<string, StringfyableParam>, config?: ApiRequestConfig): Promise<T> {
    return this.axiosClient.post<T, T>(url, body, { params, ...config });
  }

  patch<U, T>(url: string, body: U, params?: Record<string, StringfyableParam>, config?: ApiRequestConfig): Promise<T> {
    return this.axiosClient.patch<T, T>(url, body, { params, ...config });
  }

  delete<T>(url: string, params?: Record<string, StringfyableParam>, config?: ApiRequestConfig): Promise<T> {
    return this.axiosClient.delete<T, T>(url, { params, ...config });
  }
}
